import React, { useEffect, useMemo, useState } from "react";
import apiClient from "../api/apiClient";
import { formatNumberPY } from "../utils/format";

/**
 * Tipo de cambio del día (global, para todas las empresas).
 *
 * Permite cargar la cotización (fecha + moneda + tasa) que usa la emisión de
 * facturas, sin depender de que esté cargada en SAP. Muestra el histórico
 * completo (append-only en el backend) y el estado del día.
 *
 * Convención de la tasa: unidades de moneda local por 1 de moneda extranjera
 * (igual que SAP: ej. 7.500 PYG por 1 USD).
 */
const hoyISO = () => {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};

const TipoCambio = () => {
  const [fecha, setFecha] = useState(hoyISO());
  const [moneda, setMoneda] = useState("USD");
  const [tasa, setTasa] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [historial, setHistorial] = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);
  const [estado, setEstado] = useState(null);

  const usuario = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("sapUser") || "{}")?.UserName || "";
    } catch {
      return "";
    }
  }, []);

  const loadHistorial = async () => {
    setLoadingHist(true);
    try {
      const res = await apiClient.get("/tipo-cambio/historial", { params: { limit: 500 } });
      setHistorial(res?.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Error cargando historial");
    } finally {
      setLoadingHist(false);
    }
  };

  const loadEstado = async () => {
    try {
      const res = await apiClient.get("/tipo-cambio/estado-hoy");
      setEstado(res?.data || null);
    } catch {
      setEstado(null);
    }
  };

  useEffect(() => {
    loadHistorial();
    loadEstado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    setError("");
    setMessage("");
    if (!fecha || !moneda || !String(tasa).trim()) {
      setError("Completá fecha, moneda y tasa.");
      return;
    }
    setSaving(true);
    try {
      await apiClient.post("/tipo-cambio", {
        fecha,
        moneda: moneda.trim().toUpperCase(),
        tasa,
        usuario,
      });
      setMessage(`Cotización ${moneda.toUpperCase()} del ${fecha} guardada.`);
      setTasa("");
      await Promise.all([loadHistorial(), loadEstado()]);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Error guardando cotización");
    } finally {
      setSaving(false);
    }
  };

  const fmtNum = (n) => {
    if (n === null || n === undefined || n === "") return "";
    return formatNumberPY(n, 2);
  };

  const fmtTs = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return isNaN(d.getTime()) ? iso : d.toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-body">
        <h3 className="card-title mb-2 text-center">Tipo de cambio del día</h3>
        <hr className="mt-0 mb-3" />

        {estado && !estado.cargado && estado.faltantes?.length > 0 && (
          <div className="alert alert-warning">
            <strong>Falta cargar la cotización de hoy</strong> ({estado.fecha}) para:{" "}
            {estado.faltantes.join(", ")}. No se podrán emitir facturas en esas monedas hasta cargarla.
          </div>
        )}
        {estado && estado.cargado && estado.monedas_conocidas?.length > 0 && (
          <div className="alert alert-success py-2">
            Cotización de hoy ({estado.fecha}) cargada para: {estado.cargadas.join(", ")}.
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}
        {message && <div className="alert alert-success">{message}</div>}

        <form onSubmit={onSubmit}>
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Fecha</label>
              <input
                type="date"
                className="form-control"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Moneda</label>
              <input
                className="form-control text-uppercase"
                value={moneda}
                onChange={(e) => setMoneda(e.target.value)}
                placeholder="USD"
                maxLength={5}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Tasa</label>
              <input
                type="text"
                inputMode="decimal"
                className="form-control"
                value={tasa}
                onChange={(e) => setTasa(e.target.value.replace(/[^\d.,]/g, ""))}
                placeholder="Ej: 7.500 o 7500,50"
              />
              <div className="form-text">Moneda local por 1 de la extranjera (ej. PYG por USD). Decimal con coma.</div>
            </div>
            <div className="col-md-3">
              <button className="btn btn-primary w-100" type="submit" disabled={saving}>
                {saving ? "Guardando..." : "Cargar cotización"}
              </button>
            </div>
          </div>
        </form>

        <h5 className="mt-4 mb-2">Histórico de cargas</h5>
        <hr className="mt-0 mb-2" />
        <div className="d-flex justify-content-end mb-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={loadHistorial}
            disabled={loadingHist}
          >
            Refrescar
          </button>
        </div>

        {loadingHist ? (
          <div>Cargando histórico...</div>
        ) : historial.length === 0 ? (
          <div className="alert alert-info">Aún no se cargó ninguna cotización.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-striped align-middle">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Moneda</th>
                  <th className="text-end">Tasa</th>
                  <th className="text-end">Tasa anterior</th>
                  <th>Cargado</th>
                  <th>Usuario</th>
                </tr>
              </thead>
              <tbody>
                {historial.map((h, idx) => (
                  <tr key={idx}>
                    <td>{h.fecha}</td>
                    <td>{h.moneda}</td>
                    <td className="text-end">{fmtNum(h.tasa)}</td>
                    <td className="text-end text-muted">
                      {h.tasa_anterior != null ? fmtNum(h.tasa_anterior) : "—"}
                    </td>
                    <td className="small">{fmtTs(h.cargado_en)}</td>
                    <td>{h.usuario || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TipoCambio;
